import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { CategorySelector } from './CategorySelector';
import { ConsentUploader } from './ConsentUploader';
import { TargetPopulationUploader } from './TargetPopulationUploader';
import { useTranslation } from 'react-i18next';
import { InitialQuestionnaireSection } from '@/features/studies/components/InitialQuestionnaireSection';
import { EmaQuestionnaireSection } from '@/features/studies/components/EmaQuestionnaireSection';
import { QuestionnaireSchedulingForm } from '@/features/studies/components/QuestionnaireSchedulingForm';
import { useStudyFormContext } from '@/features/studies/contexts';

export const StudyEditForm: React.FC = () => {
  const {
    register,
    control,
    formState: { errors },
    studyId,
    formLoading,
  } = useStudyFormContext();
  const { t } = useTranslation();

  return (
    <Box
      component="form"
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        {t('pages.studies.sections.generalInfo')}
      </Typography>
      <Paper
        sx={{
          p: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('pages.studies.fields.contactName')}
              {...register('contactName', {
                maxLength: {
                  value: 75,
                  message: t('validation.maxLength', {
                    field: t('pages.studies.fields.contactName'),
                    length: 75,
                  }),
                },
              })}
              error={!!errors.contactName}
              helperText={errors.contactName?.message as string}
              disabled={formLoading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('pages.studies.fields.contactMail')}
              type="email"
              {...register('contactMail', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('validation.invalidEmail'),
                },
              })}
              error={!!errors.contactMail}
              helperText={errors.contactMail?.message as string}
              disabled={formLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('pages.studies.fields.name')}
              {...register('name', {
                required: t('validation.required', {
                  field: t('pages.studies.fields.name'),
                }),
                maxLength: {
                  value: 75,
                  message: t('validation.maxLength', {
                    field: t('pages.studies.fields.name'),
                    length: 75,
                  }),
                },
              })}
              error={!!errors.name}
              helperText={errors.name?.message as string}
              required
              disabled={formLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('pages.studies.fields.shortDescription')}
              multiline
              rows={2}
              {...register('shortDescription', {
                maxLength: {
                  value: 520,
                  message: t('validation.maxLength', {
                    field: t('pages.studies.fields.shortDescription'),
                    length: 520,
                  }),
                },
              })}
              error={!!errors.shortDescription}
              helperText={errors.shortDescription?.message as string}
              disabled={formLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('pages.studies.fields.longDescription')}
              multiline
              rows={4}
              {...register('longDescription', {})}
              error={!!errors.longDescription}
              helperText={errors.longDescription?.message as string}
              disabled={formLoading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CategorySelector
              control={control}
              errors={errors}
              loading={formLoading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('pages.studies.fields.subscriptionValidityDayInterval')}
              type="number"
              inputProps={{ min: 1 }}
              {...register('subscriptionValidityDayInterval', {
                min: {
                  value: 1,
                  message: t('forms.validation.minDay', { day: 1 }),
                },
                valueAsNumber: true,
              })}
              error={!!errors.subscriptionValidityDayInterval}
              helperText={
                errors.subscriptionValidityDayInterval?.message as string
              }
              disabled={formLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="allowParticipantsToViewPreviousResponses"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={formLoading}
                      />
                    }
                    label={t(
                      'pages.studies.fields.allowParticipantsToViewPreviousResponses.label',
                    )}
                  />
                  <FormHelperText>
                    {t(
                      'pages.studies.fields.allowParticipantsToViewPreviousResponses.helper',
                    )}
                  </FormHelperText>
                </Box>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('pages.studies.fields.consent')}
            </Typography>
            <ConsentUploader
              control={control}
              errors={errors}
              disabled={formLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('pages.studies.sections.targetPopulation')}
            </Typography>
            <TargetPopulationUploader
              control={control}
              errors={errors}
              disabled={formLoading}
            />
          </Grid>
        </Grid>
      </Paper>
      <Divider sx={{ my: 3 }} />
      <Controller
        control={control}
        name="initialQuestionnaire"
        render={({ field }) => (
          <InitialQuestionnaireSection
            formLoading={formLoading}
            studyId={studyId}
            field={field}
          />
        )}
      />
      <Divider sx={{ my: 3 }} />

      <EmaQuestionnaireSection
        formLoading={formLoading}
        studyId={studyId}
        control={control}
      />

      <Divider sx={{ my: 3 }} />
      <Controller
        control={control}
        name="emaQuestionnaires"
        render={({ field }) => (
          <QuestionnaireSchedulingForm questionnaires={field.value || []} />
        )}
      />
    </Box>
  );
};
